import { Mesh, Object3D, Scene } from 'three';
import { createMeshesFromMultiMaterialMesh } from 'three/examples/jsm/utils/SceneUtils.js';

import type { ExportManager } from '../export-manager.class';
import type { ARExportOptions } from '../types';

/**
 * Scale factor for converting scene units (inches) to meters for AR.
 * Both USDZ Quick Look and Scene Viewer expect meters.
 */
const AR_SCALE = 0.0254;

/**
 * Build an AR-ready temporary scene by cloning the island hierarchy, removing
 * the floor (unless requested), splitting multi-material meshes, and scaling
 * inches -> meters. Returns the temp scene plus the island clone so the caller
 * can detach after export.
 *
 * The clone is shallow — geometries and materials are shared with the live
 * scene. Exporters that mutate materials/geometries do their own deep clone
 * (USDZExporter via cloneSceneForExport, GLTFExporter internally). Callers
 * must NOT dispose the clone's resources.
 */
export const prepareARScene = (
	self: ExportManager,
	options: ARExportOptions
): [Scene, Object3D] => {
	const config = self.configAccess;
	const { islandHolder } = config;
	const { includeFloor = false } = options;

	if (!islandHolder) {
		throw new Error('Island holder not available');
	}

	const islandClone = islandHolder.clone(true);

	const tempScene = new Scene();
	tempScene.add(islandClone);

	if (!includeFloor) {
		// Foundation floor is tagged via userData.type === 'floor', not by .name.
		const toRemove: Array<Object3D> = [];
		islandClone.traverse((child) => {
			if (child.userData?.type === 'floor') {
				toRemove.push(child);
			}
		});
		toRemove.forEach((child) => child.parent?.remove(child));
	}

	// USDZExporter silently skips meshes whose `material` is an Array. Panels,
	// countertops, etc. are multi-material meshes, so we replace each one with
	// a Group of single-material sub-meshes (one per geometry group). GLB
	// exporter handles multi-material meshes natively but still benefits from
	// the split for Scene Viewer consistency.
	splitMultiMaterialMeshes(islandClone);

	islandClone.scale.set(AR_SCALE, AR_SCALE, AR_SCALE);

	tempScene.updateMatrixWorld(true);

	logARExportDiagnostics(tempScene);

	return [tempScene, islandClone];
};

/**
 * Replace every multi-material Mesh in the subtree with a Group of single-
 * material sub-meshes, using three.js's createMeshesFromMultiMaterialMesh
 * utility. This is required for USDZExporter, which only emits meshes whose
 * `material` is a single MeshStandardMaterial (not an Array).
 */
const splitMultiMaterialMeshes = (root: Object3D): void => {
	const work: Array<{ mesh: Mesh; parent: Object3D }> = [];
	root.traverse((obj) => {
		const asMesh = obj as Mesh;
		if (asMesh.isMesh && Array.isArray(asMesh.material) && obj.parent) {
			work.push({ mesh: asMesh, parent: obj.parent });
		}
	});
	for (const { mesh, parent } of work) {
		const replacement = createMeshesFromMultiMaterialMesh(mesh);
		parent.add(replacement);
		parent.remove(mesh);
	}
};

/**
 * Log a breakdown of what the USDZExporter will actually emit. USDZExporter
 * uses traverseVisible() and only exports Meshes whose material is a
 * MeshStandardMaterial (or subclass), so anything else is silently dropped.
 */
const logARExportDiagnostics = (scene: Scene): void => {
	let totalMeshes = 0;
	let visibleMeshes = 0;
	let exportable = 0;
	let nonStandardMaterial = 0;
	let nullMaterial = 0;
	let materialArray = 0;
	const typeCounts: Record<string, number> = {};
	const materialTypeCounts: Record<string, number> = {};
	const skippedSamples: Array<{ name: string; userDataType?: string; reason: string; matType?: string }> = [];

	scene.traverse((object) => {
		const type = object.type;
		typeCounts[type] = (typeCounts[type] ?? 0) + 1;

		const isMesh = (object as { isMesh?: boolean }).isMesh === true;
		if (!isMesh) return;
		totalMeshes++;

		const mat = (object as { material?: unknown }).material;
		const matCtor = mat && !Array.isArray(mat) ? (mat as { constructor: { name: string } }).constructor.name : Array.isArray(mat) ? 'Array' : 'null';
		materialTypeCounts[matCtor] = (materialTypeCounts[matCtor] ?? 0) + 1;

		const ancestorHidden = ((): boolean => {
			let node: Object3D | null = object as Object3D;
			while (node) {
				if (node.visible === false) return true;
				node = node.parent;
			}
			return false;
		})();

		if (ancestorHidden) {
			if (skippedSamples.length < 5) {
				skippedSamples.push({
					name: object.name || '(unnamed)',
					userDataType: (object.userData as { type?: string } | undefined)?.type,
					reason: 'invisible (self or ancestor)',
					matType: matCtor
				});
			}
			return;
		}
		visibleMeshes++;

		if (mat == null) {
			nullMaterial++;
			if (skippedSamples.length < 5) {
				skippedSamples.push({
					name: object.name || '(unnamed)',
					userDataType: (object.userData as { type?: string } | undefined)?.type,
					reason: 'null material',
					matType: matCtor
				});
			}
			return;
		}
		if (Array.isArray(mat)) {
			materialArray++;
			if (skippedSamples.length < 5) {
				skippedSamples.push({
					name: object.name || '(unnamed)',
					userDataType: (object.userData as { type?: string } | undefined)?.type,
					reason: 'material array (USDZExporter uses single material.isMeshStandardMaterial)',
					matType: matCtor
				});
			}
			return;
		}
		const isStandard = (mat as { isMeshStandardMaterial?: boolean }).isMeshStandardMaterial === true;
		if (!isStandard) {
			nonStandardMaterial++;
			if (skippedSamples.length < 5) {
				skippedSamples.push({
					name: object.name || '(unnamed)',
					userDataType: (object.userData as { type?: string } | undefined)?.type,
					reason: 'material is not MeshStandardMaterial',
					matType: matCtor
				});
			}
			return;
		}
		exportable++;
	});

	console.log('[AR export diagnostics] ' + JSON.stringify({
		totalMeshes,
		visibleMeshes,
		exportable,
		nonStandardMaterial,
		nullMaterial,
		materialArray,
		typeCounts,
		materialTypeCounts,
		skippedSamples
	}, null, 2));
};
